import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { usePortalApi } from "./portal-api";
import {
  type SyncModule,
  useSyncData,
  useSyncModuleRegistration,
} from "../sync";
import { daily } from "./provider";

const syncModuleId = "zielklinik-objekte";

export type ZielklinikObjekt = {
  readonly id: string;
  readonly name: string;
};

// Separate sync module rather than reusing `kartenobjekte` (which requires
// latitude/longitude for map rendering): Zielklinik-Objekte are often
// created without coordinates and would otherwise be silently dropped.
function useZielklinikObjekteSyncModule(): SyncModule<
  ReadonlyArray<ZielklinikObjekt>
> {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `tasks:${syncModuleId}`,
      defaultOptions: { schedule: { type: "periodic", interval: daily } },
      // Zielklinik objects are a new-Datenportal-only resource — the legacy
      // portal has no equivalent endpoint, so we simply don't fetch
      // anything for it.
      fetchData: useNewApi && baseUrl
        ? () => fetchZielklinikObjekteNew(portalKey, baseUrl)
        : async () => [],
    }),
    [portalKey, useNewApi, baseUrl],
  );
}

async function fetchZielklinikObjekteNew(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<ZielklinikObjekt>> {
  const response = await fetch(`${baseUrl}/api/device/sync/objects`, {
    headers: { Authorization: `Bearer ${portalKey}` },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return (data.objects ?? [])
    .filter((o: any) => o.status === "ACTIVE" && o.zielklinik)
    .map((o: any) => ({ id: o.id, name: o.name }));
}

export function useZielklinikObjekteSync() {
  const module = useZielklinikObjekteSyncModule();
  useSyncModuleRegistration(module);
}

export function useZielklinikObjekte(): ReadonlyArray<ZielklinikObjekt> {
  return useSyncData(syncModuleId) ?? [];
}
